/* eslint-disable react-refresh/only-export-components */
import React, {
    createContext,
    type ReactNode,
    useCallback,
    useContext,
    useMemo,
    useReducer,
    useRef,
} from "react";
import {
    computeSubmittedFunnelData,
    type FunnelState,
    funnelReducer,
    initialFunnelState,
} from "../components/SolarConfiguratorFunnel.reducer";
import {
    getFunnelStorageStateFromLocalStorage,
    resetFunnelDataInLocalStorage,
    saveCurrentStepCountToLocalStorage,
    saveFunnelDataToLocalStorage,
} from "../utils/LocalStorageManager";
import { FUNNEL_STEP_IDS } from "../constants/funnelSteps";
import type { AddressInputStepData } from "../components/Funnel/AddressInputStep";
import type { SolarMapStepData } from "../components/Funnel/SolarMapStep";
import type { EnterDetailsStepData } from "../components/Funnel/EnterDetailsStep";
import { createLogger } from "../lib/logger.ts";

const logger = createLogger("FunnelDataContext");

/**
 * Central management for funnel data.
 *
 * Owns the shared `FunnelData`, drives navigation through `funnelReducer`, and
 * centralizes localStorage persistence. Steps no longer receive `onUpdate` /
 * `onSubmit` props; they read and write their slice through {@link useFunnelStep}
 * and read other steps' data through the selector hooks.
 */
type FunnelDataContextValue = {
    state: FunnelState;
    /** Update a step's data without navigating. Does not persist. */
    update: (stepId: string, stepData: FunnelStepData) => void;
    /** Update a step's data, persist it, and advance to the next step. */
    submit: (stepId: string, stepData: FunnelStepData) => void;
    /** Update a step's data and persist it without navigating (incremental saves). */
    save: (stepId: string, stepData: FunnelStepData) => void;
    /** Jump to a specific step index (stepper navigation). */
    goToStep: (stepIndex: number) => void;
    /** Reset the funnel back to the first step and clear persisted data. */
    reset: () => void;
};

const FunnelDataContext = createContext<FunnelDataContextValue | null>(null);

type FunnelDataProviderProps = {
    funnelSteps: FunnelStep[];
    configuratorStep: FunnelStep;
    children: ReactNode;
};

export const FunnelDataProvider: React.FC<FunnelDataProviderProps> = ({
    funnelSteps,
    configuratorStep,
    children,
}) => {
    const [state, dispatch] = useReducer(
        funnelReducer,
        undefined,
        (): FunnelState => {
            const { stepCount, funnelData } = getFunnelStorageStateFromLocalStorage();
            const initialStep = funnelSteps[stepCount] || configuratorStep;
            return initialFunnelState(stepCount, initialStep, funnelData);
        }
    );

    // Keep latest state/config in refs so the callbacks below can stay stable
    // (no stale closures) even when used inside async step effects.
    const stateRef = useRef(state);
    stateRef.current = state;
    const funnelStepsRef = useRef(funnelSteps);
    funnelStepsRef.current = funnelSteps;
    const configuratorStepRef = useRef(configuratorStep);
    configuratorStepRef.current = configuratorStep;

    const update = useCallback((stepId: string, stepData: FunnelStepData) => {
        dispatch({ type: "UPDATE_STEP_DATA", payload: { stepId, stepData } });
    }, []);

    const submit = useCallback((stepId: string, stepData: FunnelStepData) => {
        logger.debug("Submitting step", { stepId, stepData });
        dispatch({
            type: "SUBMIT_STEP_DATA",
            payload: {
                stepId,
                stepData,
                allFunnelSteps: funnelStepsRef.current,
                configuratorStep: configuratorStepRef.current,
            },
        });

        // Persist the exact funnel data the reducer derived (incl. cross-step state).
        const newFunnelData = computeSubmittedFunnelData(
            stateRef.current.funnelData,
            stepId,
            stepData
        );
        saveFunnelDataToLocalStorage(newFunnelData);
        saveCurrentStepCountToLocalStorage(stateRef.current.currentStepIndex + 1);
    }, []);

    const save = useCallback((stepId: string, stepData: FunnelStepData) => {
        dispatch({ type: "UPDATE_STEP_DATA", payload: { stepId, stepData } });
        saveFunnelDataToLocalStorage({ ...stateRef.current.funnelData, [stepId]: stepData });
    }, []);

    const goToStep = useCallback((stepIndex: number) => {
        dispatch({
            type: "GO_TO_STEP",
            payload: { stepIndex, allFunnelSteps: funnelStepsRef.current },
        });
        saveCurrentStepCountToLocalStorage(stepIndex);
    }, []);

    const reset = useCallback((softReset: boolean = true) => {
        logger.debug("Resetting funnel data");
        dispatch({ type: "RESET", payload: { firstStep: funnelStepsRef.current[0] } });
        resetFunnelDataInLocalStorage(softReset);
    }, []);

    const value = useMemo<FunnelDataContextValue>(
        () => ({ state, update, submit, save, goToStep, reset }),
        [state, update, submit, save, goToStep, reset]
    );

    return <FunnelDataContext value={value}>{children}</FunnelDataContext>;
};

const useFunnelContext = (): FunnelDataContextValue => {
    const context = useContext(FunnelDataContext);
    if (!context) {
        throw new Error("useFunnelContext must be used within a FunnelDataProvider");
    }
    return context;
};

/** Funnel-level navigation state and actions. */
export const useFunnelNavigation = () => {
    const { state, goToStep, reset } = useFunnelContext();
    return {
        currentStepIndex: state.currentStepIndex,
        currentFunnelStep: state.currentFunnelStep,
        isAtConfiguratorStep: state.isAtConfiguratorStep,
        goToStep,
        reset,
    };
};

/** Whole funnel data, for cross-step reads. Prefer the typed selectors below. */
export const useFunnelData = (): FunnelData => useFunnelContext().state.funnelData;

export type FunnelStepHandle<T extends FunnelStepData> = {
    /** This step's persisted data, if any. */
    data: T | undefined;
    /** Update this step's data without navigating. */
    update: (stepData: T) => void;
    /** Update this step's data, persist it, and advance to the next step. */
    submit: (stepData: T) => void;
    /** Update and persist this step's data without navigating (incremental saves). */
    save: (stepData: T) => void;
};

/**
 * Read/write access to a single funnel step's slice of the central data.
 * Replaces the former `data` / `onUpdate` / `onSubmit` props.
 */
export function useFunnelStep<T extends FunnelStepData = FunnelStepData>(
    stepId: string
): FunnelStepHandle<T> {
    const { state, update, submit, save } = useFunnelContext();
    const data = state.funnelData[stepId] as T | undefined;

    return {
        data,
        update: useCallback((stepData: T) => update(stepId, stepData), [update, stepId]),
        submit: useCallback((stepData: T) => submit(stepId, stepData), [submit, stepId]),
        save: useCallback((stepData: T) => save(stepId, stepData), [save, stepId]),
    };
}

/** Typed cross-step selectors (replace unsafe `data['...'] as ...` casts). */
export const useAddressData = (): AddressInputStepData | undefined =>
    useFunnelData()[FUNNEL_STEP_IDS.ADDRESS_INPUT] as AddressInputStepData | undefined;

export const useSolarMapData = (): SolarMapStepData | undefined =>
    useFunnelData()[FUNNEL_STEP_IDS.PRODUCT_SELECTION] as SolarMapStepData | undefined;

export const useDetailsData = (): EnterDetailsStepData | undefined =>
    useFunnelData()[FUNNEL_STEP_IDS.DETAILS_INPUT] as EnterDetailsStepData | undefined;
